import {
  Button,
  Card,
  Badge,
  Space,
  Spin,
  Row,
  Col,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { BreadCrumb } from "../../components/main/LayoutComponents";
import { Locale } from "../../utils";
import {
  showNotificationWithIcon,
} from "../../utils/helpers/helpers";
import { getOne } from "../../utils/http/dataProvider";
import { getUserInfo } from "../../utils/redux/reducers/User";

import "./VideoDetail.scss";

const i18n = Locale.getInstance();

const formatDuration = (seconds) => {
  if (!seconds) return "00:00";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const VideoDetail = () => {
  const user = useSelector((state) => getUserInfo(state));
  const history = useHistory();
  const { id } = useParams();
  const [loading] = useState(false);

  const { data: video, error, isLoading } = useQuery(
    ["video", id, user.token],
    () => getOne("video", { id, token: user.token }),
    {
      enabled: !!id,
    }
  );

  if (error) {
    showNotificationWithIcon(error.data?.message || error.message);
  }

  const breadCrumbData = [
    {
      href: "/main/videos",
      title: i18n.t("video.title"),
    },
    {
      title: video ? i18n.getPropNameByLocale(video, "name") : "",
    },
  ];

  if (isLoading) {
    return <Spin spinning={isLoading} />;
  }

  if (!video) {
    return <div>{i18n.t("video.notFound")}</div>;
  }

  return (
    <div className="video-detail-page">
      <BreadCrumb data={breadCrumbData} />
      <Spin spinning={loading || isLoading}>
        <Row gutter={16}>
          <Col span={24}>
            <Card
              title={i18n.t("course.basicInfo")}
              className="video-info-card"
              extra={
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => {
                    history.push({
                      pathname: "/main/videos/edit",
                      state: { id: video.id },
                    });
                  }}
                >
                  {i18n.t("product.productItem.actionEdit")}
                </Button>
              }
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("video.name")}:</label>
                    <span>{i18n.getPropNameByLocale(video, "name")}</span>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("common.statusLabel")}:</label>
                    <Badge
                      status={video.status === 1 ? "success" : "default"}
                      text={
                        video.status === 1
                          ? i18n.t("course.album.statusOnShelf")
                          : i18n.t("course.album.statusOffShelf")
                      }
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("course.album.order")}:</label>
                    <span>{video.order || 0}</span>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("video.duration")}:</label>
                    <span>{formatDuration(video.duration)}</span>
                  </div>
                </Col>
                <Col span={24}>
                  <div className="cover-images">
                    <div className="cover-item">
                      <label>{i18n.t("video.zhCover")}:</label>
                      <img
                        src={video.zh_image || ""}
                        alt="Chinese Cover"
                        style={{ maxWidth: 200, maxHeight: 150 }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                    <div className="cover-item">
                      <label>{i18n.t("video.ugCover")}:</label>
                      <img
                        src={video.ug_image || ""}
                        alt="Uyghur Cover"
                        style={{ maxWidth: 200, maxHeight: 150 }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          {video.media && (
            <Col span={24}>
              <Card title={i18n.t("video.videoFile")} className="video-file-card">
                <div style={{ textAlign: "center" }}>
                  <video
                    controls
                    src={video.media.url}
                    style={{ maxWidth: "100%", maxHeight: "500px" }}
                  />
                  <div style={{ marginTop: 16 }}>
                    <Space>
                      <span>{i18n.t("video.fileName")}: {video.media.file_name || "-"}</span>
                      <span>{i18n.t("video.fileSize")}: {video.media.size || "-"}</span>
                    </Space>
                  </div>
                </div>
              </Card>
            </Col>
          )}

          <Col span={24}>
            <Card title={i18n.t("video.statistics")} className="statistics-card">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("video.defaultViews")}:</label>
                    <span>{video.default_views || 0}</span>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("video.views")}:</label>
                    <span>{video.views || 0}</span>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("video.defaultFavourites")}:</label>
                    <span>{video.default_favourites || 0}</span>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("video.favorites")}:</label>
                    <span>{video.favorites || 0}</span>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col span={24}>
            <Card title={i18n.t("course.album.settings")} className="settings-card">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("users.date")}:</label>
                    <span>{video.created_at}</span>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default VideoDetail;

